/**
 * Ad-platform conversion pixels (X / Twitter + Reddit).
 *
 * Buying happens off-site at a brokerage, so the closest measurable "purchase"
 * signal is a broker click-out from a fund page (primary conversion); opening a
 * fact sheet / prospectus is the secondary, higher-volume research signal. This
 * module loads the two vendor base pixels and forwards those two events — which
 * the first-party analytics hub already classifies — to `twq` / `rdt`, tagged
 * with the fund ticker so campaigns can compare cost-per-conversion per fund.
 *
 * It piggybacks on `analytics.ts`: every broker/doc click already flows through
 * `track()`, so there are ZERO markup changes here.
 *
 * Activation: the pixel ids are public (they ship in the client bundle), so the
 * live Corgi Funds ids are baked in as defaults but ONLY activate in production
 * (gated on VITE_ENV) — gated dev/staging previews and local dev stay completely
 * dormant so they never fire real conversions. An explicit VITE_*_PIXEL_ID env
 * var still overrides in any env (e.g. to test against a sandbox pixel).
 *
 * Privacy: shares the exact DNT / GPC gate as the first-party analytics
 * (`isTrackingEnabled`) so behavior is consistent across both trackers.
 */

import { isTrackingEnabled } from "@/lib/analytics";

/** Only the public production site fires pixels; every other env stays dormant. */
const IS_PROD = (import.meta.env.VITE_ENV ?? "").trim() === "production";

const X_PIXEL_ID = (import.meta.env.VITE_X_PIXEL_ID ?? (IS_PROD ? "r4tl1" : "")).trim();
const REDDIT_PIXEL_ID = (
  import.meta.env.VITE_REDDIT_PIXEL_ID ?? (IS_PROD ? "a2_icqapmrlqvsk" : "")
).trim();

/**
 * Event ids/names (overridable via env, with the live Corgi Funds defaults baked
 * in — these are public identifiers that ship in the client bundle anyway).
 *
 * X fires by EVENT ID (`tw-<pixel>-<event>`), taken from Events Manager → the
 * `<>`/Conversion-API view of each event:
 *   - broker click-out (primary)         → "broker click-out" event  tw-r4tl1-rdb9n
 *   - fact sheet / prospectus (secondary)→ the Download event        tw-r4tl1-rdb9o
 * Reddit uses its built-in standard event names (Purchase / ViewContent).
 */
const X_EVENT_BROKER = (import.meta.env.VITE_X_EVENT_BROKER ?? "tw-r4tl1-rdb9n").trim();
const X_EVENT_DOCUMENT = (import.meta.env.VITE_X_EVENT_DOCUMENT ?? "tw-r4tl1-rdb9o").trim();
const REDDIT_EVENT_BROKER = (import.meta.env.VITE_REDDIT_EVENT_BROKER ?? "Purchase").trim();
const REDDIT_EVENT_DOCUMENT = (import.meta.env.VITE_REDDIT_EVENT_DOCUMENT ?? "ViewContent").trim();

type PixelFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    twq?: PixelFn & { version?: string; queue?: unknown[]; exe?: PixelFn };
    rdt?: PixelFn & { callQueue?: unknown[]; sendEvent?: PixelFn };
  }
}

let started = false;

function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

const X_ENABLED = X_PIXEL_ID.length > 0;
const REDDIT_ENABLED = REDDIT_PIXEL_ID.length > 0;

/** Inject the official X (Twitter) universal website tag + configure the pixel. */
function loadXPixel(): void {
  if (!hasWindow() || window.twq) return;
  const w = window;
  const stub = ((...args: unknown[]) => {
    if (stub.exe) stub.exe(...args);
    else stub.queue?.push(args);
  }) as PixelFn & { version?: string; queue?: unknown[]; exe?: PixelFn };
  stub.version = "1.1";
  stub.queue = [];
  w.twq = stub;

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://static.ads-twitter.com/uwt.js";
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(s, first);

  w.twq("config", X_PIXEL_ID);
}

/** Inject the official Reddit Pixel + init + a baseline PageVisit. */
function loadRedditPixel(): void {
  if (!hasWindow() || window.rdt) return;
  const w = window;
  const stub = ((...args: unknown[]) => {
    if (stub.sendEvent) stub.sendEvent(...args);
    else stub.callQueue?.push(args);
  }) as PixelFn & { callQueue?: unknown[]; sendEvent?: PixelFn };
  stub.callQueue = [];
  w.rdt = stub;

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://www.redditstatic.com/ads/pixel.js";
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(s, first);

  w.rdt("init", REDDIT_PIXEL_ID);
  w.rdt("track", "PageVisit");
}

/**
 * Load the configured base pixels once. No-op when no ids are configured or the
 * visitor opted out (DNT / GPC). Idempotent — safe to call on every mount.
 */
export function initAdPixels(): void {
  if (started) return;
  if (!hasWindow() || !isTrackingEnabled()) return;
  if (!X_ENABLED && !REDDIT_ENABLED) return;
  started = true;
  try {
    if (X_ENABLED) loadXPixel();
    if (REDDIT_ENABLED) loadRedditPixel();
  } catch {
    // Ads code must never throw into the app.
  }
}

/** Per-fund content payload the vendors accept for reporting/segmentation. */
function xParams(fund?: string): Record<string, unknown> {
  if (!fund) return {};
  return { contents: [{ content_type: "product", content_id: fund, content_name: fund }] };
}

function redditParams(fund?: string): Record<string, unknown> {
  if (!fund) return {};
  return { products: [{ id: fund, name: fund, category: "ETF" }] };
}

/**
 * Forward a first-party analytics event to the ad pixels. Only the two
 * conversion-relevant events are mapped; everything else is ignored. Called
 * from `track()` so the fund ticker (`fundTicker`) rides along for free.
 */
export function forwardToPixels(name: string, opts: { fundTicker?: string } = {}): void {
  if (!hasWindow()) return;
  if (!X_ENABLED && !REDDIT_ENABLED) return;

  let xEvent: string | null = null;
  let redditEvent: string | null = null;
  if (name === "broker_click") {
    xEvent = X_EVENT_BROKER;
    redditEvent = REDDIT_EVENT_BROKER;
  } else if (name === "document_open") {
    xEvent = X_EVENT_DOCUMENT;
    redditEvent = REDDIT_EVENT_DOCUMENT;
  } else {
    return;
  }

  const fund = opts.fundTicker;
  try {
    if (X_ENABLED && typeof window.twq === "function") {
      window.twq("event", xEvent, xParams(fund));
    }
    if (REDDIT_ENABLED && typeof window.rdt === "function") {
      window.rdt("track", redditEvent, redditParams(fund));
    }
  } catch {
    // best-effort; never surface ad-pixel failures to the user
  }
}
